// App.tsx
import React, { useEffect } from 'react';
import { Button, Col, Layout, Row, Tag } from 'antd';
import { FoodCard } from './components/FoodCard';
import { styled } from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import UserInfo from './components/UserInfo';
import ActionButtons from './components/ActionButtons';
import AddFood from './components/AddFood';
import { useMenuQuery, useUserQuery } from './api';
import { Menu } from './api/types';
import AddFoodToMenu from './components/AddFoodToMenu';
import EmptyList from './components/EmptyList';
import { getGoogleCode, loginWithCode } from './api/user';

const { Header, Content } = Layout;

function Login() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLogedIn, setIsLogedIn] = React.useState(false);
  const [foodModalVisible, setFoodModalVisible] = React.useState(false);
  const [menuModalVisible, setMenuModalVisible] = React.useState(false);

  const { isLoading, data: menu, refetch } = useMenuQuery();
  const { data: user } = useUserQuery({ retry: 3 });

  const handleOnAddToMenu = () => {
    setMenuModalVisible(false);
    refetch();
  };

  const renderFoodCards = () => {
    if (!menu) return;

    const distinctFoods = [...menu].reduce((acc: Menu[], food: Menu) => {
      const existingFood = acc.find(
        (menuItem) => menuItem.food.externalId === food.food.externalId
      );
      if (!existingFood) {
        acc.push(food);
      }
      return acc;
    }, []);

    return distinctFoods?.map((food: Menu) => (
      <Col key={food.externalId} md={6} xs={24}>
        <FoodCard menu={food} disabled={!isLogedIn} />
      </Col>
    ));
  };

  const login = async (code: string) => {
    if (!code) return;

    const token = await loginWithCode(code);

    localStorage.setItem('token', token);
    setIsLogedIn(true);
  };

  const verifyToken = (): boolean => {
    const token = localStorage.getItem('token');

    if (token && user) return true;
    return false;
  };

  const cleanCallbackParams = () => {
    searchParams.delete('code');
    searchParams.delete('state');
    searchParams.delete('scope');
    searchParams.delete('authuser');
    searchParams.delete('hd');
    searchParams.delete('prompt');
  };

  useEffect(() => {
    if (verifyToken()) {
      setIsLogedIn(true);
    }

    const code = searchParams.get('code') || '';

    setSearchParams(searchParams);
    cleanCallbackParams();

    if (!code) return;

    login(code);
  }, [user]);

  return (
    <Layout>
      <PageHeader>
        <h1 style={{ color: 'white' }}>
          Cogteen: Canteen Food Voting For Cogniters! &nbsp;
          <Tag color='purple'>Alpha</Tag>
        </h1>
      </PageHeader>
      <PageContent>
        {isLoading ? (
          <Col>
            <Loading>Loading...</Loading>
          </Col>
        ) : (
          <>
            <Row justify='start'>
              <Col span={8}>
                <LoginPanel>
                  {isLogedIn ? (
                    <>
                      <UserInfo />
                      <ActionButtons
                        onAddToMenu={() => setMenuModalVisible(true)}
                      />
                      <AddFood
                        open={foodModalVisible}
                        onClose={() => setFoodModalVisible(false)}
                      />
                      <AddFoodToMenu
                        onAddFood={() => setFoodModalVisible(true)}
                        open={menuModalVisible}
                        onClose={() => setMenuModalVisible(false)}
                        onFinish={handleOnAddToMenu}
                      />
                    </>
                  ) : (
                    <>
                      You should&nbsp;
                      <LoginButton
                        type='primary'
                        size='large'
                        onClick={getGoogleCode}
                      >
                        Login
                      </LoginButton>
                      &nbsp;to vote!
                    </>
                  )}
                </LoginPanel>
              </Col>
            </Row>
            <Row justify='center'>
              <Col flex='auto'>
                <Row>{!menu?.length ? <EmptyList /> : renderFoodCards()}</Row>
              </Col>
            </Row>
          </>
        )}
      </PageContent>
    </Layout>
  );
}

const Loading = styled.div`
  color: #ffffff;
  font-size: 30px;
  text-align: center;
  margin-top: 100px;
`;

const PageHeader = styled(Header)`
  min-height: 100px;
  padding: 0 50px;
  height: 100%;
`;

const PageContent = styled(Content)`
  background-color: #181d2d;
  padding: 30px;
`;

const LoginButton = styled(Button)`
  background-color: #ff3b2e;
  border-color: #ff3b2e;
  color: #ffffff;
  font-weight: bold;
  &:hover {
    background-color: #ff3b2e;
    border-color: #ff3b2e;
    color: #ffffff;
  }
`;

const LoginPanel = styled.div`
  background-color: #181d2d;
  padding: 30px 0px;
  color: #ffffff;
`;

export default Login;
